import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route } from "react-router-dom";
import { Box } from "@mui/material";

import { AppDrawerPage } from "../common/app-drawer";
import { NotFoundPage, StartPage } from "../common/pages";
import { IntegrationSettingsPage } from "../admin/integration";
import { TransactionSettingsPage, RiskLevelsTab } from "../admin/transaction";
import { SystemsListPage, SystemFormPage } from "../admin/transaction/systems";
import { BusynessProcessesListPage, BusynessProcessFormPage } from "../admin/transaction/busyness-processes";
import { GroupFormPage, SystemGroupsListPage } from "../admin/transaction/system-groups";
import { UserFormPage, UsersListPage } from "../admin/users";
import { LocksPage } from "../admin/locks";
import { AdminPage } from "../admin/admin-page";
import AccessRulesPage from "../access-rules/access-rules-page.jsx";
import { SignInPage, ForbiddenPage, UserProfilePage } from "../auth/pages";
import { RequireAuth } from "../auth/require-auth";
import { RequireAction } from "../auth/require-action";
import { FunctionListPage, FunctionFormPage } from "../access-rules/functions";
import { RoleListPage, RoleFormPage } from "../critical-rules/critical-roles";
import { ProfileListPage, ProfileFormPage } from "../critical-rules/critical-profiles";
import { RiskListPage, RiskFormPage } from "../access-rules/risks";
import { MatrixHeadersListPage, MatrixHeaderFormPage } from "../access-rules/matrix-headers";
import { RoleLevelReportPage, UserLevelReportPage, UserLevelBusynessReportPage } from "../sod-reports";
import { fetchUser, fetchUserAgreementText } from "../../reducers/authSlice";
import IntegrationSchedulePage from "../admin/integration/integration-schedule-page.jsx";
import CriticalRulesPage from "../critical-rules/critical-rules-page.jsx";
import { RoleLevelModelingPage, UserLevelModelingPage } from "../reports-modeling";
import { CustomRoleListPage, CustomRoleFormPage } from "../reports-modeling/custom-roles";

import "@fontsource/mulish/400.css";
import "@fontsource/mulish/500.css";
import "@fontsource/mulish/600.css";
import "@fontsource/mulish/700.css";
import "@fontsource/mulish/800.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/700.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/500.css";
import "segoe-fonts/segoe-fonts.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./style.less";
import { CompensationProceduresListPage } from "../admin/transaction/compensation-procedures";
import { CompensationProcedureFormPage } from "../admin/transaction/compensation-procedures/compensation-procedure-form-page";
import SodSocketService from "../../services/sod-socket-service";
import { softLogOut } from "../../utils/auth";
import ForceQuitDialog from "../common/dialogs/force-quit-dialog.jsx";

const App = () => {
    const dispatch = useDispatch();
    const signedIn = useSelector((state) => state.auth.signedIn);
    const [openQuitDialog, setOpenQuitDialog] = useState(false);

    const socketService = SodSocketService.getInstance()

    useEffect(() => {
        if (!signedIn) return;

        const fetchData = () => {
            dispatch(fetchUser());
            dispatch(fetchUserAgreementText());
        };

        fetchData();
    }, [signedIn, dispatch]);

    useEffect(() => {
        socketService.subscribe("force_quit", () => {
            setOpenQuitDialog(true)
        })

        return () => {
            socketService.unsubscribe("force_quit")
        }
    }, [socketService]);

    return (
        <Box className="app">
            <Routes>
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route path="/" element={<RequireAuth />}>
                    <Route path="" element={<AppDrawerPage />}>
                        <Route path="" index element={<StartPage />} />

                        {/* user_profile */}
                        <Route path="profile" element={<UserProfilePage />} />

                        {/* sod_reports */}
                        <Route path="report-user-level/:variantName?" element={<RequireAction actionId="user_level_report" />}>
                            <Route path="" element={<UserLevelReportPage />} />
                        </Route>

                        <Route path="report-role-level/:variantName?" element={<RequireAction actionId="role_level_report" />}>
                            <Route path="" element={<RoleLevelReportPage />} />
                        </Route>

                        {/* sod busyness reports */}
                        <Route path="report-summary/:rowsForCalc?" element={<RequireAction actionId="summary_report_users" />}>
                            <Route path="" element={<UserLevelBusynessReportPage />} />
                        </Route>

                        {/* reports_modeling */}
                        <Route path="modeling-role-level/:variantName?" element={<RequireAction actionId="role_level_modeling" />}>
                            <Route path="" element={<RoleLevelModelingPage />} />
                        </Route>

                        <Route path="modeling-user-level/:variantName?" element={<RequireAction actionId="user_level_modeling" />}>
                            <Route path="" element={<UserLevelModelingPage />} />
                        </Route>
                        <Route path="custom-roles/" element={<RequireAction actionId="custom_role_maintenance" />}>
                            <Route path="" element={<CustomRoleListPage />} />
                            <Route path="form/:customRole?/:systemId?/:version?" element={<CustomRoleFormPage />} />
                        </Route>
                        {/* sod_matrix_maintenance */}

                        <Route path="functions" element={<RequireAction actionId="access_rules" />}>
                            <Route path="form/:functionId?" element={<FunctionFormPage />} />

                            <Route path="" element={<AccessRulesPage />}>
                                <Route path="" element={<FunctionListPage />} />
                            </Route>
                        </Route>

                        <Route path="risks" element={<RequireAction actionId="access_rules" />}>
                            <Route path="form/:riskId?" element={<RiskFormPage />} />

                            <Route path="" element={<AccessRulesPage />}>
                                <Route path="" element={<RiskListPage />} />
                            </Route>
                        </Route>

                        <Route path="matrix-headers" element={<RequireAction actionId="access_rules" />}>
                            <Route path="form/:matrixHeaderId?" element={<MatrixHeaderFormPage />} />

                            <Route path="" element={<AccessRulesPage />}>
                                <Route path="" element={<MatrixHeadersListPage />} />
                            </Route>
                        </Route>

                        <Route path="critical-roles" element={<RequireAction actionId="critical_rules" />}>
                            <Route path="form/:name?/:system?" element={<RoleFormPage />} />

                            <Route path="" element={<CriticalRulesPage />}>
                                <Route path="" element={<RoleListPage />} />
                            </Route>
                        </Route>

                        <Route path="critical-profiles" element={<RequireAction actionId="critical_rules" />}>
                            <Route path="form/:name?/:system?" element={<ProfileFormPage />} />

                            <Route path="" element={<CriticalRulesPage />}>
                                <Route path="" element={<ProfileListPage />} />
                            </Route>
                        </Route>

                        {/* integrations */}
                        <Route path="integrations" element={<RequireAction actionId="integrations" />}>
                            <Route path=":system?/:systemType?/:scheduleType?/schedule/:taskId?" element={<IntegrationSchedulePage />} />
                            <Route path="" element={<IntegrationSettingsPage />} />
                        </Route>

                        {/* users */}
                        <Route path="users" element={<RequireAction actionId="users" />}>
                            <Route path="form/:username?" element={<UserFormPage />} />
                            <Route path="" element={<UsersListPage />} />
                        </Route>
                        
                        {/* systems */}
                        <Route path="systems" element={<RequireAction actionId="systems_settings" />}>
                            <Route path="form/:systemId?" element={<SystemFormPage />} />

                            <Route path="" element={<TransactionSettingsPage />}>
                                <Route path="" element={<SystemsListPage />} />
                            </Route>
                        </Route>

                        {/* systems-groups */}
                        <Route path="system-groups" element={<RequireAction actionId="systems_settings" />}>
                            <Route path="form/:groupId?" element={<GroupFormPage />} />

                            <Route path="" element={<TransactionSettingsPage />}>
                                <Route path="" element={<SystemGroupsListPage />} />
                            </Route>
                        </Route>

                        <Route path="risk-levels" element={<RequireAction actionId="risk_levels_settings" />}>
                            <Route path="" element={<RiskLevelsTab />} />
                        </Route>

                        {/* bp */}
                        <Route path="busyness-processes" element={<RequireAction actionId="business_process_maintenance" />}>
                            <Route path="form/:busynessProcessId?" element={<BusynessProcessFormPage />} />

                            <Route path="" element={<BusynessProcessesListPage />} />
                        </Route>

                        <Route path="compensation-procedures" element={<RequireAction actionId={"compensation_procedures"} />}>
                            <Route path="form/:compensationProcedureId?" element={<CompensationProcedureFormPage />} />
                            <Route path="" element={<CompensationProceduresListPage />}></Route>
                        </Route>

                        <Route path="locks" element={<RequireAction actionId="locks_settings" />}>
                            <Route path="" element={<LocksPage />} />
                        </Route>

                        <Route path="admin" element={<RequireAction actionId="admin_settings" />}>
                            <Route path="" element={<AdminPage />} />
                        </Route>

                    </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {openQuitDialog && <ForceQuitDialog
                onClose={() => {
                    setOpenQuitDialog(false)
                    softLogOut()
                }}
                open={openQuitDialog}
            />}
        </Box>
    );
};

export default App;
